import type {
  SimpleAlignment,
  SimpleBlockQuoteBlock,
  SimpleDocument,
  SimpleFillableTextContent,
  SimpleInlineFieldType,
  SimpleItem,
  SimplePage,
  SimplePageAppearance,
  SimplePageLayoutMode,
  SimpleSmartBlockItem,
  SimpleSmartTextContent,
  SimpleTextContent,
  SimpleTextNode,
  SimpleTextStyle,
} from './simple-builder-schema';
import { isCoverVeilIntensity } from './cover-veil';
import {
  CONTENT_BLOCK_TYPES,
  LITE_INLINE_FONT_SIZE_RANGE,
  SMART_BLOCK_TYPES,
} from './transformer-constants';

type RecordValue = Record<string, any>;
type InlineMarks = {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  fontSize?: string | number;
};

const TEXT_BLOCK_KEYS = ['h1', 'h2', 'h3', 'p'] as const;
const LIST_BLOCK_KEYS = ['bullets', 'numbered'] as const;
const FILLABLE_FIELD_TYPES: readonly SimpleInlineFieldType[] = [
  'text_input',
  'name',
  'email',
  'date',
  'number',
  'checkbox',
  'dropdown',
  'textarea',
];
const PAGE_LAYOUT_MODES: readonly SimplePageLayoutMode[] = ['left', 'right', 'top', 'background'];
// [size=40]$186,000[/size]: enlarges a run of text, for example the number on a metric card.
const INLINE_SIZE_PATTERN = String.raw`\[size=(\d{1,3})\]([\s\S]+?)\[\/size\]`;

function isRecord(value: unknown): value is RecordValue {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function isTextNode(value: unknown): value is SimpleTextNode {
  return isRecord(value) && ('text' in value || 'var' in value || 'link' in value);
}

function normalizeFontSizeValue(value: unknown): string | number | undefined {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    const num = Number(trimmed);
    if (!isNaN(num)) return num;
    return trimmed;
  }
  return undefined;
}

function normalizeInlineMarks(value: RecordValue): InlineMarks {
  const rawFontSize = normalizeFontSizeValue(
    value.fontSize ?? value['font-size'] ?? value.font_size,
  );
  return {
    ...(value.bold ? { bold: true } : {}),
    ...(value.italic ? { italic: true } : {}),
    ...(value.underline ? { underline: true } : {}),
    ...(typeof value.color === 'string' ? { color: value.color } : {}),
    ...(rawFontSize !== undefined ? { fontSize: rawFontSize } : {}),
  };
}

function parseTextSegment(text: string, marks: InlineMarks = {}): SimpleTextContent {
  const nodes: SimpleTextContent = [];
  const variableRegex = /\[\[([^\]]+)\]\]/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = variableRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ text: text.slice(lastIndex, match.index), ...marks });
    }

    const variableKey = match[1]?.trim();
    if (variableKey) {
      nodes.push({ var: variableKey, ...marks });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push({ text: text.slice(lastIndex), ...marks });
  }

  return nodes;
}

function parseBoldSegments(text: string, marks: InlineMarks): SimpleTextContent {
  const nodes: SimpleTextContent = [];
  const boldRegex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...parseTextSegment(text.slice(lastIndex, match.index), marks));
    }

    nodes.push(...parseTextSegment(match[1] ?? '', { ...marks, bold: true }));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(...parseTextSegment(text.slice(lastIndex), marks));
  }

  return nodes;
}

function parseStringToTextContent(text: string): SimpleTextContent {
  if (!text) return [{ text: '' }];

  const nodes: SimpleTextContent = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  const sizeRegex = new RegExp(INLINE_SIZE_PATTERN, 'g');

  while ((match = sizeRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(...parseBoldSegments(text.slice(lastIndex, match.index), {}));
    }

    const size = Number(match[1]);
    const inRange =
      size >= LITE_INLINE_FONT_SIZE_RANGE.min && size <= LITE_INLINE_FONT_SIZE_RANGE.max;
    nodes.push(...parseBoldSegments(match[2] ?? '', inRange ? { fontSize: `${size}px` } : {}));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(...parseBoldSegments(text.slice(lastIndex), {}));
  }

  return nodes.length > 0 ? nodes : [{ text: '' }];
}

// Callouts and tables pass through to the simple transformer, which has no size marker.
// Keep their text and drop the marker so it never shows up as literal brackets.
function stripInlineSizeMarkers<T>(value: T): T {
  if (typeof value === 'string') {
    return value.replace(new RegExp(INLINE_SIZE_PATTERN, 'g'), '$2') as T;
  }
  if (Array.isArray(value)) return value.map(stripInlineSizeMarkers) as T;
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, stripInlineSizeMarkers(entry)]),
    ) as T;
  }
  return value;
}

function normalizeTextNode(value: unknown): SimpleTextContent {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return parseStringToTextContent(String(value));
  }

  if (!isRecord(value)) {
    return [{ text: '' }];
  }

  if ('text' in value) {
    return [
      {
        text: String(value.text ?? ''),
        ...normalizeInlineMarks(value),
      },
    ];
  }

  if ('var' in value) {
    return [
      {
        var: String(value.var ?? ''),
        ...normalizeInlineMarks(value),
      },
    ];
  }

  if ('link' in value) {
    return [
      {
        link: String(value.link ?? ''),
        content: normalizeTextContent(value.content),
      },
    ];
  }

  return [{ text: '' }];
}

function normalizeTextContent(value: unknown): SimpleTextContent {
  if (Array.isArray(value)) {
    return value.flatMap((node) => normalizeTextNode(node));
  }

  if (isRecord(value) && 'content' in value) {
    return normalizeTextContent(value.content);
  }

  return normalizeTextNode(value);
}

function normalizeListContent(value: unknown): SimpleTextContent[] {
  const items = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.items)
      ? value.items
      : [];

  return items.map((item) => normalizeTextContent(item));
}

function normalizeTextStyle(...styles: unknown[]): SimpleTextStyle | undefined {
  const mergedRaw = Object.assign(
    {},
    ...styles.filter((style): style is Record<string, unknown> => isRecord(style)),
  );

  const rawFontSize = normalizeFontSizeValue(
    mergedRaw.fontSize ?? mergedRaw['font-size'] ?? mergedRaw.font_size,
  );
  const rawColor = typeof mergedRaw.color === 'string' ? mergedRaw.color : undefined;
  const rawBgColor =
    typeof (mergedRaw.backgroundColor ?? mergedRaw['background-color'] ?? mergedRaw.bgColor) ===
    'string'
      ? (mergedRaw.backgroundColor ?? mergedRaw['background-color'] ?? mergedRaw.bgColor)
      : undefined;
  const rawAlign =
    typeof (mergedRaw.align ?? mergedRaw['text-align'] ?? mergedRaw.textAlign) === 'string'
      ? (mergedRaw.align ?? mergedRaw['text-align'] ?? mergedRaw.textAlign)
      : undefined;
  const rawListStyle =
    typeof (mergedRaw.listStyle ?? mergedRaw['list-style'] ?? mergedRaw.list_style) === 'string'
      ? (mergedRaw.listStyle ?? mergedRaw['list-style'] ?? mergedRaw.list_style)
      : undefined;

  const normalized: Record<string, unknown> = {
    ...mergedRaw,
    ...(rawFontSize !== undefined ? { fontSize: rawFontSize } : {}),
    ...(rawColor !== undefined ? { color: rawColor } : {}),
    ...(rawBgColor !== undefined ? { backgroundColor: rawBgColor } : {}),
    ...(rawAlign !== undefined ? { align: rawAlign } : {}),
    ...(rawListStyle !== undefined ? { listStyle: rawListStyle } : {}),
  };

  delete normalized['font-size'];
  delete normalized['font_size'];
  delete normalized['background-color'];
  delete normalized['bgColor'];
  delete normalized['text-align'];
  delete normalized['textAlign'];
  delete normalized['list-style'];
  delete normalized['list_style'];

  const filtered = Object.fromEntries(
    Object.entries(normalized).filter(([, v]) => v !== undefined),
  );

  return Object.keys(filtered).length > 0 ? (filtered as SimpleTextStyle) : undefined;
}

function extractItemInlineStyle(item: RecordValue): Record<string, unknown> | undefined {
  const style: Record<string, unknown> = {};
  const rawFontSize = normalizeFontSizeValue(item.fontSize ?? item['font-size'] ?? item.font_size);
  if (rawFontSize !== undefined) style.fontSize = rawFontSize;
  if (typeof item.color === 'string') style.color = item.color;
  const align = item.align ?? item.textAlign ?? item['text-align'];
  if (typeof align === 'string') style.align = align;
  return Object.keys(style).length > 0 ? style : undefined;
}

function normalizeTextBlock(item: RecordValue): SimpleItem | null {
  const itemInlineStyle = extractItemInlineStyle(item);
  for (const key of TEXT_BLOCK_KEYS) {
    if (!(key in item)) continue;

    const value = item[key];
    const normalized: RecordValue = {
      [key]: normalizeTextContent(value),
    };
    const style = normalizeTextStyle(
      isRecord(value) ? value.style : undefined,
      item.style,
      itemInlineStyle,
    );
    if (style) normalized.style = style;
    return normalized as SimpleItem;
  }

  for (const key of LIST_BLOCK_KEYS) {
    if (!(key in item)) continue;

    const value = item[key];
    const normalized: RecordValue = {
      [key]: normalizeListContent(value),
    };
    const style = normalizeTextStyle(
      isRecord(value) ? value.style : undefined,
      item.style,
      itemInlineStyle,
    );
    if (style) normalized.style = style;
    return normalized as SimpleItem;
  }

  return null;
}

function normalizeBlockQuote(value: unknown): SimpleBlockQuoteBlock {
  if (isRecord(value) && 'content' in value) {
    return {
      block_quote: {
        content: normalizeTextContent(value.content),
        ...(value.style ? { style: value.style } : {}),
      },
    };
  }

  return { block_quote: normalizeTextContent(value) };
}

function normalizeSmartText(value: unknown): SimpleSmartTextContent {
  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => isRecord(item) && !isTextNode(item))
  ) {
    return value
      .map((item) => normalizeLiteJsonItem(item))
      .filter((item): item is SimpleItem => !!item) as SimpleSmartTextContent;
  }

  return normalizeTextContent(value);
}

function normalizeSmartBlockItem(value: unknown): SimpleSmartBlockItem {
  const item = isRecord(value) ? value : {};

  return {
    title: normalizeSmartText(item.title ?? ''),
    description: normalizeSmartText(item.description ?? ''),
    ...(typeof item.icon === 'string' ? { icon: item.icon } : {}),
    ...(typeof item.color === 'string' ? { color: item.color } : {}),
    ...(typeof item.bgColor === 'string' ? { bgColor: item.bgColor } : {}),
  };
}

const GRID_SMART_BLOCK_TYPES = new Set<string>([
  'process_flow',
  'icon_text',
  'top_circle_boxes',
  'solid_boxes',
  'side_line_boxes',
  'quote_boxes',
]);

const GRID_COLUMNS: Record<string, 'single' | 'two' | 'three'> = {
  one: 'single',
  two: 'two',
  three: 'three',
};

function normalizeSmartBlockValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeSmartBlockItem);
  }

  if (isRecord(value)) {
    return {
      ...value,
      items: Array.isArray(value.items) ? value.items.map(normalizeSmartBlockItem) : [],
    };
  }

  return [];
}

function normalizeFillableFieldType(value: unknown): SimpleInlineFieldType {
  const type = typeof value === 'string' ? value : 'text_input';
  return FILLABLE_FIELD_TYPES.includes(type as SimpleInlineFieldType)
    ? (type as SimpleInlineFieldType)
    : 'text_input';
}

function normalizeFieldOptions(
  value: unknown,
): Array<{ label: string; value: string }> | undefined {
  if (!Array.isArray(value)) return undefined;

  const options = value
    .map((option) => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }

      if (isRecord(option) && typeof option.label === 'string') {
        return {
          label: option.label,
          value: typeof option.value === 'string' ? option.value : option.label,
        };
      }

      return null;
    })
    .filter((option): option is { label: string; value: string } => option !== null);

  return options.length > 0 ? options : undefined;
}

function normalizeFillableFieldNode(node: RecordValue): SimpleFillableTextContent[number] {
  const fieldValue = node.field;
  const fieldConfig = isRecord(fieldValue) ? fieldValue : node;
  const type = normalizeFillableFieldType(isRecord(fieldValue) ? fieldValue.type : fieldValue);
  const label =
    typeof fieldConfig.label === 'string'
      ? fieldConfig.label
      : typeof fieldConfig.placeholder === 'string'
        ? fieldConfig.placeholder
        : undefined;
  const options = normalizeFieldOptions(fieldConfig.options);

  return {
    field: {
      type,
      ...(label ? { placeholder: label } : {}),
      ...(typeof fieldConfig.required === 'boolean' ? { required: fieldConfig.required } : {}),
      ...(options ? { options } : {}),
      ...(typeof fieldConfig.multiSelect === 'boolean'
        ? { multiSelect: fieldConfig.multiSelect }
        : {}),
      ...(fieldConfig.min !== undefined ? { min: fieldConfig.min } : {}),
      ...(fieldConfig.max !== undefined ? { max: fieldConfig.max } : {}),
    },
  };
}

function normalizeFillableTextContent(value: unknown): SimpleFillableTextContent {
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return parseStringToTextContent(String(value));
  }

  if (!Array.isArray(value)) {
    return [{ text: '' }];
  }

  const normalized: SimpleFillableTextContent = [];

  for (const node of value) {
    if (isRecord(node) && 'field' in node) {
      normalized.push(normalizeFillableFieldNode(node));
      continue;
    }

    normalized.push(...normalizeTextNode(node));
  }

  return normalized;
}

function normalizeFillableTextBlock(value: unknown): SimpleItem {
  return { fillable_text: normalizeFillableTextContent(value) };
}

function normalizeColumns(value: unknown): { columns: SimpleItem[][]; widths?: number[] } {
  if (!Array.isArray(value)) return { columns: [] };

  const widths: number[] = [];
  const columns = value.map((column) => {
    if (!isRecord(column) || !Array.isArray(column.content)) return [];

    if (typeof column.width === 'number') {
      widths.push(column.width);
    }

    return column.content
      .map((item) => normalizeLiteJsonItem(item))
      .filter((item): item is SimpleItem => !!item);
  });

  return {
    columns,
    ...(widths.length === columns.length ? { widths } : {}),
  };
}

function normalizeContentBlockItemIds(
  value: unknown,
): true | { layout?: string; items?: string[] } | null {
  if (value === true) return true;

  if (Array.isArray(value)) {
    const items = value.filter(
      (item): item is string => typeof item === 'string' && item.length > 0,
    );
    return items.length > 0 ? { items } : true;
  }

  if (isRecord(value)) {
    const nextOptions: { layout?: string; items?: string[] } = {};
    if (typeof value.layout === 'string') {
      nextOptions.layout = value.layout;
    }
    if (Array.isArray(value.items)) {
      const items = value.items.filter(
        (item): item is string => typeof item === 'string' && item.length > 0,
      );
      if (items.length > 0) {
        nextOptions.items = items;
      }
    }
    return Object.keys(nextOptions).length > 0 ? nextOptions : true;
  }

  return null;
}

function normalizeLabelBlock(value: unknown): SimpleItem | null {
  if (!isRecord(value) || !Array.isArray(value.items)) return null;

  const labels = value.items
    .map((item) => {
      if (typeof item === 'string' && item.length > 0) {
        return item;
      }

      if (!isRecord(item) || typeof item.text !== 'string' || item.text.length === 0) {
        return null;
      }

      return {
        text: item.text,
        ...(item.variant === 'outline' || item.variant === 'solid'
          ? { variant: item.variant }
          : {}),
        ...(typeof item.icon === 'string' ? { icon: item.icon } : {}),
      };
    })
    .filter(
      (item): item is string | { text: string; variant?: 'solid' | 'outline'; icon?: string } =>
        item !== null,
    );

  return labels.length > 0
    ? ({
        label: {
          labels,
          ...(typeof value.fitToWidth === 'boolean' ? { fullWidth: value.fitToWidth } : {}),
          ...(value.align === 'center' || value.align === 'right' ? { align: value.align } : {}),
        },
      } as SimpleItem)
    : null;
}

function normalizePassthroughItem(item: RecordValue, key: string): SimpleItem {
  return { [key]: item[key] } as SimpleItem;
}

export function normalizeLiteJsonItem(item: unknown): SimpleItem | null {
  if (!isRecord(item)) return null;

  const textBlock = normalizeTextBlock(item);
  if (textBlock) return textBlock;

  if ('text' in item && Array.isArray(item.text)) {
    return {
      text: item.text
        .map((child) => normalizeLiteJsonItem(child))
        .filter((child): child is SimpleItem => !!child),
      ...(item.style ? { style: item.style } : {}),
    } as SimpleItem;
  }

  if ('block_quote' in item) return normalizeBlockQuote(item.block_quote);
  if ('fillable_text' in item) return normalizeFillableTextBlock(item.fillable_text);
  if ('editor_fillable_text' in item) {
    return { editor_fillable_text: normalizeFillableTextContent(item.editor_fillable_text) };
  }
  if ('label' in item) return normalizeLabelBlock(item.label);

  for (const key of SMART_BLOCK_TYPES) {
    if (key in item) {
      const value = normalizeSmartBlockValue(item[key]);
      if (!GRID_SMART_BLOCK_TYPES.has(key)) return { [key]: value } as SimpleItem;

      const items = Array.isArray(value) ? value : (value as RecordValue).items;
      const requested = isRecord(value) ? GRID_COLUMNS[value.columns as string] : undefined;
      // Without a choice, four cards read better as a 2x2 grid than as a row of three plus one.
      const columns = requested ?? (items.length === 4 ? 'two' : undefined);
      const align =
        isRecord(value) && ['left', 'center', 'right'].includes(value.align) ? value.align : undefined;
      if (!columns && !align) return { [key]: items } as SimpleItem;
      return {
        [key]: { items, ...(columns ? { columns } : {}), ...(align ? { align } : {}) },
      } as SimpleItem;
    }
  }

  for (const key of CONTENT_BLOCK_TYPES) {
    if (key in item) {
      const contentBlockValue = normalizeContentBlockItemIds(item[key]);
      return contentBlockValue ? ({ [key]: contentBlockValue } as SimpleItem) : null;
    }
  }

  if ('columns' in item) {
    const normalizedColumns = normalizeColumns(item.columns);
    return {
      columns: normalizedColumns.columns,
      ...(normalizedColumns.widths ? { widths: normalizedColumns.widths } : {}),
    };
  }

  const passthroughKeys = [
    'image',
    'video',
    'button',
    'icon',
    'code',
    'divider',
    'chart',
    'pricing_table',
    'quotation_builder',
    'space',
    'page_break',
    'table_of_contents',
    'editor_signature_block',
    'recipient_signature_block',
    'recipient_initials_block',
    'recipient_stamp_block',
    'editor_initials_block',
    'editor_stamp_block',
  ];

  if ('callout_box' in item) return { callout_box: stripInlineSizeMarkers(item.callout_box) };
  if ('table' in item) return { table: stripInlineSizeMarkers(item.table) };

  for (const key of passthroughKeys) {
    if (key in item) return normalizePassthroughItem(item, key);
  }

  return null;
}

const sameHexColor = (a: string, b: string) => {
  const expand = (hex: string) => {
    const value = hex.trim().toLowerCase().replace(/^#/, '');
    return value.length === 3 ? [...value].map((char) => char + char).join('') : value;
  };
  return expand(a) === expand(b);
};

function normalizePageLayout(
  page: RecordValue,
  canvasColor?: string,
): SimplePageAppearance | undefined {
  const layout = page.layout;

  if (!isRecord(layout)) {
    return undefined;
  }

  const modeValue = layout.mode;
  if (!PAGE_LAYOUT_MODES.includes(modeValue as SimplePageLayoutMode)) {
    return undefined;
  }

  const mode = modeValue as SimplePageLayoutMode;
  const media: Record<string, unknown> = {};
  const hasSourceImageRef = isRecord(layout.image) && typeof layout.image.ref === 'string';
  // layout.image is either a prompt string (legacy) or { prompt?, ref? }. Keep the object
  // shape so the converter can resolve a ref to a source-image URL, mirroring image blocks.
  if (typeof layout.image === 'string') {
    media.image = layout.image;
  } else if (
    isRecord(layout.image) &&
    (layout.image.prompt || layout.image.ref || layout.image.url)
  ) {
    // A server-delivered image (FEAT-042) carries only `url` — the prompt was
    // cleared so the client effect stays inert. Dropping it here would lose the
    // image entirely, so url counts as a keeper too.
    media.image = layout.image;
  }

  if (layout.fit === 'contain' || layout.fit === 'cover') {
    media.fit = layout.fit;
  } else if (hasSourceImageRef) {
    // Source graphics are often logos, diagrams, or screenshots where silent cropping destroys
    // information. The model can explicitly opt photographs into cover when that is intentional.
    media.fit = 'contain';
  }

  const color = typeof layout.color === 'string' ? layout.color : undefined;
  // A page filled with the canvas color loses its edges against the canvas. Drop it so the page
  // keeps the theme's page surface. On `background` the color is the tint, so it stays.
  const pageColor =
    color && mode !== 'background' && !(canvasColor && sameHexColor(color, canvasColor))
      ? color
      : undefined;

  return {
    ...(pageColor ? { card: { mode: 'solid' as const, value: pageColor } } : {}),
    layout: {
      mode,
      ...(mode === 'top'
        ? { size: { height: typeof layout.height === 'number' ? layout.height : 250 } }
        : {}),
      ...(mode === 'left' || mode === 'right'
        ? { size: { width: typeof layout.width === 'number' ? layout.width : 320 } }
        : {}),
      ...(Object.keys(media).length > 0 ? { media } : {}),
      ...(mode === 'background'
        ? {
            overlay:
              layout.tint === false
                ? { effect: 'clear', intensity: 0 }
                : {
                    effect: 'faded',
                    // Set by the server for a cover it veiled; the model's schema has no such key.
                    intensity: isCoverVeilIntensity(layout.tintIntensity)
                      ? layout.tintIntensity
                      : 60,
                    color: color ?? '#000000',
                  },
          }
        : {}),
    },
  };
}

export function normalizeLiteJsonPage(
  page: unknown,
  index: number = 0,
  canvasColor?: string,
): SimplePage {
  const value = isRecord(page) ? page : {};
  const content = Array.isArray(value.content) ? value.content : [];
  const appearance = normalizePageLayout(value, canvasColor);

  return {
    name:
      typeof value.name === 'string' && value.name.trim().length > 0
        ? value.name
        : `Page ${index + 1}`,
    ...(appearance ? { appearance } : {}),
    ...(isRecord(value._preserve) ? { _preserve: value._preserve } : {}),
    content: content
      .map((item) => normalizeLiteJsonItem(item))
      .filter((item): item is SimpleItem => !!item),
  };
}

export function normalizeLiteJsonDocument(document: unknown, canvasColor?: string): SimpleDocument {
  if (!Array.isArray(document)) return [];
  return document.map((page, index) => normalizeLiteJsonPage(page, index, canvasColor));
}
